import { useState, FC, useRef, RefObject, useEffect } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions, Paper, IconButton, Box, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { BalanceDominoCreate, BalanceDomino } from 'repository/timekeeper/@types/BalanceDomino';
import { TipoCascadaEnum } from 'repository/timekeeper/@types/TypeCascadeEnum';
import { BalanceDominoForm } from './BalanceDominoForm';


interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: BalanceDomino[];
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

export const CreateBalanceDomino: FC<CreateProps> = ({ open, setOpen, rows, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<BalanceDominoCreate>({
        name: '',
        description: null,
        date_pattern_id: 0,
        type_accrual: TipoAcumulacionEnum.HORAS,
        accrual_code_id: null,
        fixed_amount: 0,
        percentage_cascade: 0,
        type_cascade: TipoCascadaEnum.REDUCIR_A_CERO,
        cascade_orders: [],
    });

    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)
    const [datePatterns, setDatePatterns] = useState<any[]>([])
    const [accrualCodes, setAccrualCodes] = useState<any[]>([])

    useEffect(() => {
        const loadData = async () => {
            try {
                const [datePatternsList, accrualCodesList] = await Promise.all([
                    timekeeperService.date_pattern.getAll(),
                    timekeeperService.accrual_code.getAll()
                ]);
                setDatePatterns(datePatternsList);
                setAccrualCodes(accrualCodesList);
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };
        if (open) {
            loadData();
        }
    }, [open]);

    const changes = () => {
        if (
            data.name !== ''
        ) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        setData({
            name: '',
            description: null,
            date_pattern_id: 0,
            type_accrual: TipoAcumulacionEnum.HORAS,
            accrual_code_id: null,
            fixed_amount: 0,
            percentage_cascade: 0,
            type_cascade: TipoCascadaEnum.REDUCIR_A_CERO,
            cascade_orders: [],
        });
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const Create = async () => {
        setLoading(true);

        try {
            // Crear todos los registros
            console.log('Code to create:', data);
            const createdBalanceDomino = await timekeeperService.balance_domino.create(data);
            console.log('Created Balance Domino:', createdBalanceDomino);

            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    }

    const handleDataChange = (newData: any) => {
        setData(newData as BalanceDominoCreate);
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.balance_domino.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <BalanceDominoForm
                        data={data}
                        setData={handleDataChange}
                        rows={rows}
                        t={t}
                        datePatterns={datePatterns}
                        accrualCodes={accrualCodes}
                        mode="create"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={() => Create()} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};